// src/utils/stockAlertsUtils.js

// 📊 Stock Status Constants
export const STOCK_STATUS = {
  OUT_OF_STOCK: 'out_of_stock',
  LOW_STOCK: 'low_stock',
  NORMAL: 'normal',
  OVER_STOCK: 'over_stock'
}

// 🏷️ Item Type Constants
export const ITEM_TYPES = {
  PRODUCT: 'product',
  RAW_MATERIAL: 'raw_material'
}

// 🎨 Status Colors and Labels
export const STATUS_CONFIG = {
  [STOCK_STATUS.OUT_OF_STOCK]: {
    label: 'Out of Stock',
    badgeColor: 'bg-red-100 text-red-800',
    bgColor: 'bg-red-50 border-red-200',
    textColor: 'text-red-900',
    iconColor: 'text-red-500',
    priority: 3
  },
  [STOCK_STATUS.LOW_STOCK]: {
    label: 'Low Stock',
    badgeColor: 'bg-yellow-100 text-yellow-800',
    bgColor: 'bg-yellow-50 border-yellow-200',
    textColor: 'text-yellow-900',
    iconColor: 'text-yellow-500',
    priority: 2
  },
  [STOCK_STATUS.NORMAL]: {
    label: 'Normal',
    badgeColor: 'bg-green-100 text-green-800',
    bgColor: 'bg-green-50 border-green-200',
    textColor: 'text-green-900',
    iconColor: 'text-green-500',
    priority: 1
  },
  [STOCK_STATUS.OVER_STOCK]: {
    label: 'Over Stock',
    badgeColor: 'bg-blue-100 text-blue-800',
    bgColor: 'bg-blue-50 border-blue-200',
    textColor: 'text-blue-900',
    iconColor: 'text-blue-500',
    priority: 0
  }
}

// 🏷️ Item Type Labels
export const ITEM_TYPE_LABELS = {
  [ITEM_TYPES.PRODUCT]: 'Product',
  [ITEM_TYPES.RAW_MATERIAL]: 'Raw Material'
}

/**
 * 📊 Get stock status based on current stock and threshold
 * @param {number} currentStock - Current stock amount
 * @param {number} threshold - Minimum stock threshold
 * @returns {string} Stock status
 */
export const getStockStatus = (currentStock, threshold) => {
  if (currentStock === 0) {
    return STOCK_STATUS.OUT_OF_STOCK
  } else if (currentStock <= threshold) {
    return STOCK_STATUS.LOW_STOCK
  } else if (currentStock > threshold * 3) {
    return STOCK_STATUS.OVER_STOCK
  } else {
    return STOCK_STATUS.NORMAL
  }
}

/**
 * 🎨 Get status configuration
 * @param {string} status - Stock status
 * @returns {object} Status configuration
 */
export const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || STATUS_CONFIG[STOCK_STATUS.NORMAL]
}

/**
 * 🎯 Get stock status info with full details
 * @param {number} currentStock - Current stock amount
 * @param {number} threshold - Minimum stock threshold
 * @returns {object} Complete status information
 */
export const getStockStatusInfo = (currentStock, threshold) => {
  const status = getStockStatus(currentStock, threshold)
  const config = getStatusConfig(status)
  
  return {
    status,
    ...config,
    currentStock,
    threshold,
    shortfall: status === STOCK_STATUS.LOW_STOCK ? threshold - currentStock : 0,
    isAlert: status === STOCK_STATUS.OUT_OF_STOCK || status === STOCK_STATUS.LOW_STOCK
  }
}

/**
 * 🔍 Filter alerts by multiple criteria
 * @param {Array} alerts - Stock alerts array
 * @param {Object} filters - Filter criteria
 * @returns {Array} Filtered alerts
 */
export const filterAlerts = (alerts, filters) => {
  let filtered = [...alerts]

  // Filter by status
  if (filters.status && filters.status !== 'all') {
    filtered = filtered.filter(alert => alert.stockStatus === filters.status)
  }

  // Filter by item type
  if (filters.itemType && filters.itemType !== 'all') {
    filtered = filtered.filter(alert => alert.itemType === filters.itemType)
  }

  // Filter by branch
  if (filters.branchId && filters.branchId !== 'all') {
    filtered = filtered.filter(alert => {
      const alertBranchId = alert.branch?._id || alert.branch
      return alertBranchId === filters.branchId
    })
  }

  // Search by name/code
  if (filters.searchTerm) {
    const term = filters.searchTerm.toLowerCase()
    filtered = filtered.filter(alert => {
      const itemName = alert.name || ''
      const itemCode = alert.sku || alert.code || ''
      const branchName = alert.branch?.name || ''
      
      return itemName.toLowerCase().includes(term) ||
             itemCode.toLowerCase().includes(term) ||
             branchName.toLowerCase().includes(term)
    })
  }

  return filtered
}

/**
 * 🔄 Sort alerts by specified criteria
 * @param {Array} alerts - Stock alerts array
 * @param {string} sortBy - Field to sort by
 * @param {string} sortOrder - Sort order (asc/desc)
 * @returns {Array} Sorted alerts
 */
export const sortAlerts = (alerts, sortBy = 'name', sortOrder = 'asc') => {
  return [...alerts].sort((a, b) => {
    let valueA, valueB

    // Handle nested properties
    if (sortBy.includes('.')) {
      const keys = sortBy.split('.')
      valueA = keys.reduce((obj, key) => obj?.[key], a)
      valueB = keys.reduce((obj, key) => obj?.[key], b)
    } else {
      // Handle special sorting cases
      switch (sortBy) {
        case 'priority':
          valueA = getStatusConfig(a.stockStatus).priority
          valueB = getStatusConfig(b.stockStatus).priority
          break
        case 'shortfall':
          valueA = a.threshold - a.currentStock
          valueB = b.threshold - b.currentStock
          break
        default:
          valueA = a[sortBy]
          valueB = b[sortBy]
      }
    }
    
    // Handle string comparison
    if (typeof valueA === 'string') {
      valueA = valueA.toLowerCase()
      valueB = valueB?.toLowerCase() || ''
    }
    
    // Handle null/undefined values
    if (valueA == null) valueA = sortOrder === 'asc' ? '' : 0
    if (valueB == null) valueB = sortOrder === 'asc' ? '' : 0
    
    if (sortOrder === 'asc') {
      return valueA < valueB ? -1 : valueA > valueB ? 1 : 0
    } else {
      return valueA > valueB ? -1 : valueA < valueB ? 1 : 0
    }
  })
}

/**
 * 📊 Calculate comprehensive summary statistics
 * @param {Array} alerts - Stock alerts array
 * @returns {Object} Summary statistics
 */
export const calculateAlertsSummary = (alerts) => {
  const summary = {
    totalItems: alerts.length,
    byStatus: {
      outOfStock: 0,
      lowStock: 0,
      normal: 0,
      overStock: 0
    },
    byType: {
      products: 0,
      rawMaterials: 0
    },
    byBranch: {},
    criticalItems: [],
    totalValue: 0,
    affectedBranches: new Set()
  }

  alerts.forEach(alert => {
    // Count by status
    switch (alert.stockStatus) {
      case STOCK_STATUS.OUT_OF_STOCK:
        summary.byStatus.outOfStock++
        break
      case STOCK_STATUS.LOW_STOCK:
        summary.byStatus.lowStock++
        break
      case STOCK_STATUS.NORMAL:
        summary.byStatus.normal++
        break
      case STOCK_STATUS.OVER_STOCK:
        summary.byStatus.overStock++
        break
    }

    // Count by type
    if (alert.itemType === ITEM_TYPES.PRODUCT) {
      summary.byType.products++
    } else if (alert.itemType === ITEM_TYPES.RAW_MATERIAL) {
      summary.byType.rawMaterials++
    }

    // Count by branch
    const branchId = alert.branch?._id || 'unknown'
    const branchName = alert.branch?.name || 'Unknown Branch'
    
    if (!summary.byBranch[branchId]) {
      summary.byBranch[branchId] = {
        name: branchName,
        count: 0,
        outOfStock: 0,
        lowStock: 0
      }
    }
    
    summary.byBranch[branchId].count++
    if (alert.stockStatus === STOCK_STATUS.OUT_OF_STOCK) {
      summary.byBranch[branchId].outOfStock++
    } else if (alert.stockStatus === STOCK_STATUS.LOW_STOCK) {
      summary.byBranch[branchId].lowStock++
    }

    // Add to affected branches
    summary.affectedBranches.add(branchId)

    // Collect critical items
    if (alert.stockStatus === STOCK_STATUS.OUT_OF_STOCK) {
      summary.criticalItems.push({
        id: alert._id,
        name: alert.name,
        code: alert.sku || alert.code,
        type: alert.itemType,
        branch: branchName,
        threshold: alert.threshold
      })
    }

    // Calculate total value (if unit prices are available)
    if (alert.unitPrice && alert.currentStock) {
      summary.totalValue += alert.unitPrice * alert.currentStock
    }
  })

  // Convert affected branches to count
  summary.affectedBranchesCount = summary.affectedBranches.size

  return summary
}

/**
 * 🎯 Get priority sorted alerts (critical first)
 * @param {Array} alerts - Stock alerts array
 * @returns {Array} Priority sorted alerts
 */
export const getPriorityAlerts = (alerts) => {
  return sortAlerts(alerts, 'priority', 'desc')
}

/**
 * 📋 Format alert item for display
 * @param {Object} alert - Stock alert item
 * @returns {Object} Formatted alert item
 */
export const formatAlertItem = (alert) => {
  const statusInfo = getStockStatusInfo(alert.currentStock, alert.threshold)
  const itemCode = alert.sku || alert.code
  const unitName = alert.unit?.name || 'Units'
  const unitAbbr = alert.unit?.abbreviation || 'U'

  return {
    ...alert,
    statusInfo,
    itemCode,
    unitDisplay: `${unitName} (${unitAbbr})`,
    formattedStock: `${alert.currentStock.toLocaleString()} ${unitAbbr}`,
    formattedThreshold: `Min: ${alert.threshold}${unitAbbr}`,
    isProduct: alert.itemType === ITEM_TYPES.PRODUCT,
    isRawMaterial: alert.itemType === ITEM_TYPES.RAW_MATERIAL
  }
}

/**
 * 📊 Get filter options from alerts data
 * @param {Array} alerts - Stock alerts array
 * @returns {Object} Available filter options
 */
export const getFilterOptions = (alerts) => {
  const options = {
    branches: [],
    statuses: Object.keys(STATUS_CONFIG).map(status => ({
      value: status,
      label: STATUS_CONFIG[status].label
    })),
    itemTypes: [
      { value: ITEM_TYPES.PRODUCT, label: ITEM_TYPE_LABELS[ITEM_TYPES.PRODUCT] },
      { value: ITEM_TYPES.RAW_MATERIAL, label: ITEM_TYPE_LABELS[ITEM_TYPES.RAW_MATERIAL] }
    ]
  }

  // Extract unique branches
  const branchesMap = new Map()
  alerts.forEach(alert => {
    if (alert.branch) {
      branchesMap.set(alert.branch._id, {
        value: alert.branch._id,
        label: alert.branch.name
      })
    }
  })
  options.branches = Array.from(branchesMap.values())

  return options
}

/**
 * 🔄 Export data utilities
 */
export const exportUtils = {
  /**
   * Convert alerts to CSV format
   */
  toCSV: (alerts) => {
    const headers = ['Name', 'Code', 'Type', 'Current Stock', 'Threshold', 'Status', 'Branch', 'Unit']
    const rows = alerts.map(alert => [
      alert.name,
      alert.sku || alert.code,
      ITEM_TYPE_LABELS[alert.itemType],
      alert.currentStock,
      alert.threshold,
      STATUS_CONFIG[alert.stockStatus].label,
      alert.branch?.name || '',
      alert.unit?.abbreviation || ''
    ])

    return [headers, ...rows].map(row => row.join(',')).join('\n')
  },

  /**
   * Convert alerts to JSON format
   */
  toJSON: (alerts) => {
    return JSON.stringify(alerts.map(formatAlertItem), null, 2)
  }
}